import { lazy } from "react";

// React.lazy() that survives a redeploy. Every build renames its route chunks
// (content hashes), and Railway's `serve -s build` answers a request for a
// chunk that no longer exists with index.html (200, text/html) - so a tab
// opened before a deploy fails the first time it navigates to a page it
// hasn't loaded yet (e.g. homepage -> an artist profile): the import rejects,
// React unmounts the whole tree, and the page is left blank until a manual
// refresh fetches the new index.html. Do that refresh automatically - the
// URL has already changed, so the reload lands on the page that was clicked.
//
// At most one automatic reload per RELOAD_WINDOW_MS, so a chunk that's
// genuinely unreachable (offline, server down) falls through to
// RouteErrorBoundary instead of reloading forever.
const RELOAD_KEY = "nexxup:chunkReloadAt";
const RELOAD_WINDOW_MS = 10000;

function tryReloadOnce() {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY)) || 0;
    if (Date.now() - last < RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // No sessionStorage means no loop guard - don't risk a reload loop.
    return false;
  }
  window.location.reload();
  return true;
}

export default function lazyWithReload(factory) {
  return lazy(() =>
    factory().catch((error) => {
      // Never resolves: the Suspense fallback stays up until the reload.
      if (tryReloadOnce()) return new Promise(() => {});
      throw error;
    })
  );
}
